/**
 * Parcours démo client guidée — BT-033 (source de vérité routes + comptes).
 * Documentation : 00_PILOTAGE/KARATE-DEMO-CLIENT-GUIDEE-PHASE1-033.md
 * Prérequis : BT-025 → BT-032 PASS, coquille permanente BT-030.
 */

import { DEMO_USER_REGISTRY } from "@/data/dojoOsOrgModel.js";
import { getPlayerTabItems, getParentNavItems } from "@/config/karateNavConfig.js";
import { SHOTOKAN_DOCUMENTS_PREUVES_BASE } from "@/pages/documentsPreuves/documentsPreuvesNavConfig.js";
import { getAllTrainingTracks } from "@/content/trainingContent.js";

export const DEMO_GUIDE_VERSION = "1.6.10-phase1";
export const DEMO_GUIDE_BT_ID = "BT-GSD-KARATE-DEMO-CLIENT-GUIDEE-PHASE1-033";
export const DEMO_ESTIMATED_DURATION = "50–65 min";

export const DEMO_PREREQUISITE_BTS = [
  "BT-025 — Navigation admin regroupée",
  "BT-026 — Navigation Sensei / Sempai / Étudiant / Parent",
  "BT-027 — Audit cohérence Phase 1",
  "BT-028-R3 — FR/EN 100 % DOM visible",
  "BT-029-R2 — Header / logo Shotokan AAMC",
  "BT-030 — Coquille permanente i18n + aide",
  "BT-032 — Formation par rôle",
];

/** Comptes locaux — mot de passe : demo */
export const DEMO_CLIENT_ACCOUNTS = Object.values(DEMO_USER_REGISTRY).map((u) => ({
  username: u.username,
  password: u.password,
  role: u.role,
  labelFr: u.nameFr,
  labelEn: u.nameEn,
  canSwitchRoles: (u.accessibleRoles?.length ?? 0) > 1,
}));

/** Les 3 espaces GSD — à expliquer au client. */
export const DEMO_GSD_SPACES_FR = [
  {
    id: "administration",
    title: "Administration (Hanshi)",
    body: "Vue globale, organisations, dojos, personnes, progression, Shotokan Black Dragon, Documents & Preuves, import.",
  },
  {
    id: "gestionnaire",
    title: "Gestionnaire (Sensei / Sempai)",
    body: "Gestion dojo au quotidien : élèves, cours, événements, progression — permissions différentes Sensei vs Sempai.",
  },
  {
    id: "joueur",
    title: "Joueur (Étudiant / Parent)",
    body: "Espace simple en onglets : consultation personnelle, documents autorisés, événements en mode démo.",
  },
];

export const DEMO_GSD_SPACES_EN = [
  {
    id: "administration",
    title: "Administration (Hanshi)",
    body: "Global view, organisations, dojos, people, progress, Shotokan Black Dragon, Documents & Evidence, import.",
  },
  {
    id: "gestionnaire",
    title: "Manager (Sensei / Sempai)",
    body: "Daily dojo management: students, classes, events, progress — different Sensei vs Sempai permissions.",
  },
  {
    id: "joueur",
    title: "Player (Student / Parent)",
    body: "Simple tabbed space: personal view, allowed documents, events in demo mode.",
  },
];

/** Limites Phase 1 — verbal obligatoire. */
export const PHASE1_DEMO_LIMITS_FR = [
  "Jeu initial minimal BT-046 [DÉMO] — noms fournis (Gilles, Marcel, Natalie) ; 10 dojos et responsables à confirmer.",
  "Aucune donnée officielle inventée — grades, adresses et listes complètes en attente.",
  "Aucun document réel importé — import contrôlé en statut « reçu — validation requise ».",
  "Aucun backend documents sécurisé en Phase 1.",
  "Paiements non gérés — écrans placeholder ou absents.",
  "Présences non opérationnelles — suivi léger mock local.",
  "Ne pas présenter une fonction démo comme opérationnelle ou certifiée.",
];

export const PHASE1_DEMO_LIMITS_EN = [
  "Minimal initial dataset BT-046 [DEMO] — provided names (Gilles, Marcel, Natalie); 10 dojos and leads to confirm.",
  "No official data invented — belts, addresses and full lists still pending.",
  "No real documents imported — controlled import status: received — validation required.",
  "No secure documents backend in Phase 1.",
  "Payments not managed — placeholder screens or absent.",
  "Attendance not operational — light local mock only.",
  "Do not present demo functionality as operational or certified.",
];

/** Ce qui viendra en Phase 2 — à mentionner sans promettre de date. */
export const PHASE2_PREVIEW_FR = [
  "Données officielles validées après réception et import du fichier client.",
  "Backend documents sécurisé et workflow de validation complet.",
  "Présences et paiements opérationnels si requis par le client.",
  "Communications réelles dojo ↔ membres.",
  "Certification / examens officiels selon périmètre convenu.",
];

export const PHASE2_PREVIEW_EN = [
  "Validated official data after client file receipt and import.",
  "Secure documents backend and full validation workflow.",
  "Operational attendance and payments if required by the client.",
  "Real dojo ↔ member communications.",
  "Official certification / exams per agreed scope.",
];

export const PRE_DEMO_CHECKLIST = [
  "Application locale (npm run dev) — version ≥ 1.6.10-phase1 visible.",
  "Bandeau [DÉMO] visible — ne pas le masquer, l'expliquer dès le début.",
  "Session déconnectée — repartir de /.",
  "Desktop plein écran + onglet mobile 390 px prêt.",
  "Comptes démo : admin, sensei, sempai, etudiant, parent — mot de passe demo.",
  "Aucun document réel à importer pendant la démo.",
  "Guide BT-033 ou second écran ouvert.",
  "Captures evidence disponibles si machine instable.",
  "Prévoir 2–3 bascules FR/EN + ouverture Formation + Aide.",
];

export const DEMO_HIGHLIGHTS_FR = [
  "Coquille GSD premium : 3 espaces (Administration, Gestionnaire, Joueur).",
  "Bilingue FR/EN complet — pages, aide, formation, données démo.",
  "Aide contextualisée (écran courant) + Formation (apprentissage par rôle).",
  "Navigation admin regroupée BT-025 — Shotokan + Documents & Preuves intégrés.",
  "Navigation Sensei / Sempai / Étudiant / Parent BT-026 — permissions claires.",
  "Header / logo Shotokan AAMC BT-029-R2 — identité visuelle cohérente.",
  "10 fonds photo variés — rotation à la navigation.",
  "Import CSV + import contrôlé — workflow validation Hanshi visible.",
  "Formation par rôle BT-032 — 4 parcours prêts pour les utilisateurs finaux.",
];

export const DEMO_CLIENT_SECTIONS = [
  {
    id: "public",
    titleFr: "1. Portail public",
    titleEn: "1. Public portal",
    steps: [
      { route: "/", labelFr: "Accueil", sayFr: "Phase 1 : ce qui est prêt, ce qui est [DÉMO], ce qui viendra en Phase 2. Structure Hanshi → Sensei → dojos.", sayEn: "Phase 1: what's ready, what's [DEMO], what comes in Phase 2. Hanshi → Sensei → dojos structure." },
      { route: "/dojos", labelFr: "Dojos", sayFr: "Répertoire public — emplacements [DÉMO], 10 dojos objectif après fichier client.", sayEn: "Public directory — [DEMO] slots, 10 dojos goal after client file." },
      { route: "/instructeurs", labelFr: "Instructeurs", sayFr: "Leadership reçu — associations dojo à confirmer avec vous.", sayEn: "Leadership received — dojo links to confirm with you." },
      { route: "/etudiants", labelFr: "Étudiants", sayFr: "Jeu initial minimal — Marcel Charette et Natalie Lavigne (grades à confirmer) ; enfant [DÉMO] pour Parent seulement.", sayEn: "Minimal initial dataset — Marcel Charette and Natalie Lavigne (belts to confirm); [DEMO] child for Parent space only." },
      { route: "/documents", labelFr: "Documents", sayFr: "Visibilités public / membres / admin — rien publié sans validation Hanshi.", sayEn: "Public / members / admin visibility — nothing without Hanshi validation." },
      { route: "/protocoles", labelFr: "Protocoles", sayFr: "Contenu sensible — mode prudent, validation requise.", sayEn: "Sensitive content — careful mode, validation required." },
      { route: "/demande-compte", labelFr: "Demande de compte", sayFr: "Formulaire Phase 1 — enregistrement local [DÉMO], pas d'envoi réel.", sayEn: "Phase 1 form — local [DEMO] save, no real sending." },
      { route: "/connexion", labelFr: "Connexion", sayFr: "Comptes démo — mot de passe : demo. Admin peut simuler tous les rôles.", sayEn: "Demo accounts — password: demo. Admin can simulate all roles." },
    ],
  },
  {
    id: "coquille",
    titleFr: "2. Coquille globale GSD",
    titleEn: "2. GSD global shell",
    steps: [
      { route: "/dojos", labelFr: "FR / EN", sayFr: "Toggle langue — contenu traduit, badges [DÉMO]/[DEMO] localisés.", sayEn: "Language toggle — translated content, localized [DEMO] badges.", showFr: "Basculer EN puis revenir FR" },
      { route: "/admin/instructeurs", labelFr: "Aide contextualisée", sayFr: "Le bouton Aide explique l'écran courant — titre dédié, pas de texte générique.", sayEn: "Help button explains current screen — dedicated title, not generic.", showFr: "Ouvrir Aide sur Instructeurs", auth: { username: "admin", role: "hanshi" } },
      { route: "/formation", labelFr: "Formation par rôle", sayFr: "L'onglet Formation explique comment utiliser l'app selon le rôle — complémentaire à l'Aide.", sayEn: "Training tab explains how to use the app by role — complementary to Help.", showFr: "Montrer les 4 cartes Formation" },
      { route: "/", labelFr: "Header / logo", sayFr: "Logo Shotokan AAMC agrandi, une ligne claire — identité BT-029-R2.", sayEn: "Enlarged Shotokan AAMC logo, clear one-line brand — BT-029-R2.", showFr: "Header desktop + mobile" },
      { route: "/connexion", labelFr: "Images de fond", sayFr: "10 fonds photo — rotation à chaque navigation, blocs lisibles par-dessus.", sayEn: "10 photo backgrounds — rotate on navigation, readable blocks on top.", showFr: "Naviguer 2–3 pages pour voir le changement" },
      { route: "/admin", labelFr: "3 espaces GSD", sayFr: "Administration, Gestionnaire, Joueur — trois coquilles distinctes selon le rôle.", sayEn: "Administration, Manager, Player — three distinct shells by role.", showFr: "Sélecteur Mode admin → Sensei → Étudiant", auth: { username: "admin", role: "hanshi" } },
    ],
  },
  {
    id: "admin",
    titleFr: "3. Administration Hanshi",
    titleEn: "3. Hanshi admin",
    auth: { username: "admin", role: "hanshi" },
    steps: [
      { route: "/admin", labelFr: "Tableau de bord", sayFr: "Vue d'ensemble Phase 1, compteurs [DÉMO], préparation fichier client.", sayEn: "Phase 1 overview, [DEMO] counters, client file preparation." },
      { route: "/admin/organisation", labelFr: "Organisations", sayFr: "Structure org — données [DÉMO], 10 dojos objectif.", sayEn: "Org structure — [DEMO] data, 10 dojos goal." },
      { route: "/admin/dojos", labelFr: "Dojos", sayFr: "Gestion emplacements — clairement marqués [DÉMO].", sayEn: "Slot management — clearly marked [DEMO]." },
      { route: "/admin/instructeurs", labelFr: "Instructeurs / Sensei", sayFr: "Leadership et titres reçus — assignations à confirmer.", sayEn: "Leadership and received titles — assignments to confirm." },
      { route: "/admin/assistants", labelFr: "Assistants / Sempai", sayFr: "Comptes assistant par dojo.", sayEn: "Assistant accounts per dojo." },
      { route: "/admin/etudiants", labelFr: "Étudiants", sayFr: "Jeu initial minimal — Marcel et Natalie ; import manuel ou massif ultérieur.", sayEn: "Minimal initial dataset — Marcel and Natalie; manual or bulk import later." },
      { route: "/admin/parents", labelFr: "Parents", sayFr: "Lien parent–enfant, espace parent séparé.", sayEn: "Parent–child link, separate parent space." },
      { route: "/admin/progression", labelFr: "Progression", sayFr: "Ceintures et examens légers — pas de certification officielle Phase 1.", sayEn: "Light belts and exams — no official certification Phase 1." },
      { route: "/admin/shotokan-black-dragon", labelFr: "Shotokan Black Dragon", sayFr: "Titres, demandes, reconnaissances — périmètre martial.", sayEn: "Titles, requests, recognitions — martial scope." },
      { route: SHOTOKAN_DOCUMENTS_PREUVES_BASE, labelFr: "Documents & Preuves", sayFr: "Dans Shotokan seulement — pas de backend sécurisé Phase 1.", sayEn: "Inside Shotokan only — no secure backend Phase 1." },
      { route: `${SHOTOKAN_DOCUMENTS_PREUVES_BASE}/import`, labelFr: "Import contrôlé", sayFr: "Aperçu → statut reçu — validation requise. Aucun document réel.", sayEn: "Preview → received — validation required. No real document.", showFr: "Démo certificat-demo.pdf fictif" },
    ],
  },
  {
    id: "gestionnaire",
    titleFr: "4. Gestionnaire — Sensei & Sempai",
    titleEn: "4. Manager — Sensei & Sempai",
    steps: [
      { route: "/sensei", labelFr: "Espace Sensei", sayFr: "Responsable dojo — progression, examens, titres côté Sensei.", sayEn: "Dojo lead — progress, exams, Sensei-side titles.", auth: { username: "sensei", role: "sensei" } },
      { route: "/sempai", labelFr: "Espace Sempai", sayFr: "Assistant — consultation et notes, sans validation titre ni grade.", sayEn: "Assistant — consult and notes, no title or grade validation.", auth: { username: "sempai", role: "assistant" } },
      { route: "/sempai/shotokan-black-dragon/parcours", labelFr: "Sempai — sans validation", sayFr: "Parcours titres en lecture — le Sempai ne valide pas.", sayEn: "Titles path read-only — Sempai does not validate.", auth: { username: "sempai", role: "assistant" }, showFr: "Mention « sans validation »" },
    ],
  },
  {
    id: "joueur",
    titleFr: "5. Joueur — Étudiant & Parent",
    titleEn: "5. Player — Student & Parent",
    steps: [
      { route: "/mon-espace", labelFr: "Étudiant — 7 onglets", sayFr: "Navigation simple — pas d'écrans admin.", sayEn: "Simple navigation — no admin screens.", auth: { username: "etudiant", role: "student" }, showFr: "Parcourir les onglets" },
      { route: "/mes-documents", labelFr: "Documents étudiant", sayFr: "Documents autorisés seulement — mode [DÉMO].", sayEn: "Allowed documents only — [DEMO] mode.", auth: { username: "etudiant", role: "student" } },
      { route: "/mes-evenements", labelFr: "Événements étudiant", sayFr: "Calendrier démo — pas opérationnel.", sayEn: "Demo calendar — not operational.", auth: { username: "etudiant", role: "student" } },
      { route: "/parent", labelFr: "Espace parent", sayFr: "Suivi enfant — horaires, progression, documents.", sayEn: "Child follow-up — schedule, progress, documents.", auth: { username: "parent", role: "parent" } },
      { route: "/parent/communications", labelFr: "Communications parent", sayFr: "Messages démo — envoi réel Phase 2.", sayEn: "Demo messages — real sending Phase 2.", auth: { username: "parent", role: "parent" } },
      { route: "/parent/evenements", labelFr: "Événements parent", sayFr: "Événements démo — présences non opérationnelles.", sayEn: "Demo events — attendance not operational.", auth: { username: "parent", role: "parent" } },
    ],
  },
  {
    id: "formation",
    titleFr: "6. Formation par rôle",
    titleEn: "6. Role-based training",
    steps: [
      { route: "/formation", labelFr: "Hub Formation", sayFr: "4 parcours : Enfants/Étudiants, Parents, Responsables, Administrateur.", sayEn: "4 paths: Children/Students, Parents, Managers, Administrator.", showFr: "4 cartes visibles" },
      { route: "/formation", labelFr: "Parcours Étudiant", sayFr: "Former les jeunes utilisateurs — étapes, limites [DÉMO], liens écrans.", sayEn: "Train young users — steps, [DEMO] limits, screen links.", showFr: "Ouvrir carte Enfants/Étudiants" },
      { route: "/formation", labelFr: "Parcours Responsables", sayFr: "Sensei vs Sempai — permissions et interdictions de validation.", sayEn: "Sensei vs Sempai — permissions and validation bans.", showFr: "Ouvrir carte Responsables" },
      { route: "/formation", labelFr: "Parcours Administrateur", sayFr: "Former le Hanshi — org, Shotokan, D&P, Phase 2.", sayEn: "Train Hanshi — org, Shotokan, D&E, Phase 2.", showFr: "Ouvrir carte Administrateur", auth: { username: "admin", role: "hanshi" } },
    ],
  },
  {
    id: "limites",
    titleFr: "7. Limites & Phase 2",
    titleEn: "7. Limits & Phase 2",
    steps: [
      { route: "/", labelFr: "Limites Phase 1", sayFr: "Seed minimal BT-046 en place — pas de docs réels, pas de backend sécurisé, pas de paiements, présences mock ; import massif à venir.", sayEn: "Minimal BT-046 seed in place — no real docs, no secure backend, no payments, mock attendance; bulk import later." },
      { route: "/", labelFr: "Phase 2", sayFr: "Données officielles, documents sécurisés, présences/paiements si requis, communications réelles — à convenir ensemble.", sayEn: "Official data, secure documents, attendance/payments if required, real communications — to agree together." },
    ],
  },
];

export function getDemoClientRouteIndex() {
  const routes = [];
  for (const section of DEMO_CLIENT_SECTIONS) {
    for (const step of section.steps) {
      if (!routes.includes(step.route)) routes.push(step.route);
    }
  }
  return routes;
}

export function getDemoPlayerTabCount(locale = "fr") {
  return getPlayerTabItems(locale).length;
}

export function getDemoParentTabCount(locale = "fr") {
  return getParentNavItems(locale).length;
}

export function getDemoTrainingTrackCount(locale = "fr") {
  return getAllTrainingTracks(locale).length;
}

export const DEMO_CAPTURE_SUGGESTIONS = [
  { id: "01-accueil", route: "/", label: "Accueil + périmètre Phase 1" },
  { id: "02-dojos-en", route: "/dojos", label: "Dojos EN + [DEMO]", locale: "en" },
  { id: "03-connexion", route: "/connexion", label: "Comptes démo" },
  { id: "04-aide", route: "/admin/instructeurs", label: "Aide contextualisée", auth: "admin" },
  { id: "05-formation", route: "/formation", label: "Formation 4 cartes", auth: "admin" },
  { id: "06-admin", route: "/admin", label: "Dashboard Hanshi", auth: "admin" },
  { id: "07-shotokan-dp", route: SHOTOKAN_DOCUMENTS_PREUVES_BASE, label: "Documents & Preuves Shotokan", auth: "admin" },
  { id: "08-import", route: `${SHOTOKAN_DOCUMENTS_PREUVES_BASE}/import`, label: "Import contrôlé aperçu", auth: "admin" },
  { id: "09-sensei", route: "/sensei", label: "Espace Sensei", auth: "sensei" },
  { id: "10-sempai", route: "/sempai", label: "Espace Sempai", auth: "sempai" },
  { id: "11-etudiant", route: "/mon-espace", label: "7 onglets étudiant", auth: "etudiant" },
  { id: "12-parent", route: "/parent", label: "Espace parent", auth: "parent" },
  { id: "13-mobile", route: "/formation", label: "Mobile Formation", auth: "etudiant", viewport: "mobile" },
];
